<script lang="ts">
  import Message from '@pin/components/Message.svelte';
  import type {
    ClientToServerEvent as OverlayClientToServerEvents,
    ServerToClientEvents as OverlayServerToClientEvents,
  } from '@pin/schemas/overlay';
  import type { UserCount } from '@pin/schemas/common';
  import type { ChatMessageAdmin } from '@pin/schemas/socket';
  import { format } from 'date-fns';
  import { nb } from 'date-fns/locale';
  import type { Socket } from 'socket.io-client';
  import { onMount } from 'svelte';
  import { SvelteSet } from 'svelte/reactivity';
  import { fly } from 'svelte/transition';
  import { pollUserCounts } from '../../api/api';
  import { debounce } from '../../utils/debounce';
  import { type ChatMessage, shownInFeed } from '../../utils/history';
  import Pause from '../Icons/Pause.svelte';
  import MessageActionButtons from '../MessageActionButtons.svelte';
  import WriteMessage from './WriteMessage.svelte';

  interface Props {
    overlaySocket: Socket<
      OverlayServerToClientEvents,
      OverlayClientToServerEvents
    > | null;
    chatMessages: ChatMessage[];
    userFeedUser: ChatMessageAdmin['user'] | null;
    /** Not following the live end: scrolled up or acting on a message. */
    paused?: boolean;
    onLoadHistory: (take: number) => Promise<void>;
  }
  let {
    overlaySocket,
    chatMessages,
    // eslint-disable-next-line no-useless-assignment -- This is a false positive
    userFeedUser = $bindable(),
    // eslint-disable-next-line no-useless-assignment -- This is a false positive
    paused = $bindable(false),
    onLoadHistory,
  }: Props = $props();

  let chatBox: HTMLUListElement | null = null;

  // New messages are rendered in batches, at most every 500 ms, and
  // highlighted briefly.
  const highlightedIds = new SvelteSet<string>();
  let renderedMessages = $state.raw<ChatMessage[]>([]);
  let renderTimeout: ReturnType<typeof setTimeout> | undefined;
  const renderBatch = () => {
    renderTimeout = undefined;
    const renderedIds = new Set(renderedMessages.map(({ id }) => id));
    renderedMessages = chatMessages.filter(shownInFeed);
    const batch = renderedMessages
      .map(({ id }) => id)
      .filter((id) => !renderedIds.has(id));
    for (const id of batch) {
      highlightedIds.add(id);
    }
    setTimeout(() => {
      for (const id of batch) {
        highlightedIds.delete(id);
      }
    }, 150);
  };

  let selectedUser = $state<ChatMessageAdmin['user'] | null>(null);
  let scrolling = $state(false);
  let connectedUsercounts = $state<UserCount>({
    total: 0,
    anonymous: 0,
    authenticated: 0,
  });
  let loadHistoryInProgress = $state(false);
  // By message: one row's buttons going idle must not end the pause while
  // another row's menu is still open
  const moderatingIds = new SvelteSet<string>();
  let moderationActive = $derived(moderatingIds.size > 0);
  $effect(() => {
    paused = scrolling || moderationActive;
  });

  const scrollToBottom = () => {
    scrolling = false;
    chatBox?.scrollTo(0, chatBox.scrollHeight);
  };
  const debouncedScrollToBottom = debounce(() => {
    if (!scrolling && !moderationActive) scrollToBottom();
  }, 100);

  const isScrolling = () => {
    if (chatBox == null) {
      return false;
    }
    const marginOfError = 30;
    return (
      chatBox.clientHeight + chatBox.scrollTop <
      chatBox.scrollHeight - marginOfError
    );
  };

  onMount(() => {
    const userCountSubscription = pollUserCounts().subscribe({
      next: (data) => (connectedUsercounts = data),
      error: (err) => console.error('Usercounts subscription error', err),
    });
    return () => {
      userCountSubscription.unsubscribe();
      clearTimeout(renderTimeout);
    };
  });

  // Keep the user's follow state across DOM updates and temporary moderation
  // pauses. Measuring the gap here would treat incoming messages as scrolling up.
  $effect(() => {
    if (renderedMessages.length && !scrolling && !moderationActive) {
      scrollToBottom();
    }
  });

  onMount(() => {
    if (!chatBox || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (!scrolling && !moderationActive) scrollToBottom();
    });
    observer.observe(chatBox);
    return () => observer.disconnect();
  });

  // A live message, history and the trim at the limit each change an end of
  // the list, so reading the ends is enough. Reading every message would
  // make each new one cost a pass over the whole feed.
  $effect(() => {
    void [chatMessages.length, chatMessages[0], chatMessages.at(-1)];
    renderTimeout ??= setTimeout(renderBatch, 500);
  });
</script>

<svelte:window
  onresize={() => {
    if (!scrolling) {
      debouncedScrollToBottom();
    }
  }}
/>
<div class="container">
  <h2 class="header">Public Chat</h2>
  <button
    class="btn load-more-btn"
    disabled={loadHistoryInProgress}
    aria-busy={loadHistoryInProgress}
    onclick={async () => {
      loadHistoryInProgress = true;
      await onLoadHistory(1000);
      loadHistoryInProgress = false;
    }}
  >
    Load history
    {#if loadHistoryInProgress}
      <span class="spinner" aria-hidden="true"></span>
    {/if}</button
  >
  <p class="user-counts">
    <span title="Anonymous">🕵️: {connectedUsercounts.anonymous}</span>
    |
    <span title="Authenticated">👤: {connectedUsercounts.authenticated}</span>
  </p>
  <ul
    class="messages"
    bind:this={chatBox}
    onscroll={() => {
      scrolling = isScrolling();
    }}
  >
    {#each renderedMessages as message (message.id)}
      <li
        class="message"
        class:colored-user={selectedUser?.id === message.user.id}
        class:banned={message.banned}
        class:unbanned={message.unbanned}
        class:muted={message.muted}
        class:unmuted={message.unmuted}
        class:faded={message.autoModerated && message.severeCategories == null}
        class:severe={message.severeCategories != null}
        class:highlighted={highlightedIds.has(message.id)}
        in:fly={{
          duration: 150,
          x: 100,
        }}
      >
        <div class="message-row">
          <div class="message-content">
            <button
              class="name-btn"
              class:admin-user={message.role === 'admin'}
              onclick={() => (userFeedUser = message.user)}
            >
              {message.user.name}
              {#if message.user.birthYear != null}
                ({message.user.birthYear})
              {/if}
            </button>: <Message text={message.text} />
          </div>
          {#if message.autoModerated}
            <span class="automoderated" title="Auto">🤖</span>
          {/if}
        </div>
        <span class="sent-time">
          Sent: {format(message.ts, 'dd.MM.yy HH:mm:ss', { locale: nb })}
        </span>
        {#if !message.autoModerated && (message.redacted || message.timeVisibleMs != null)}
          <div class="message-info">
            {#if message.redacted}
              <span class="message-info red">Redacted</span>
            {/if}
            {#if message.timeVisibleMs}
              {#if message.redacted}<span class="message-info faded">|</span
                >{/if}
              <span class="message-info faded">
                Visible for {Math.floor(message.timeVisibleMs / 1000)}s {message.timeVisibleMs %
                  1000}ms
              </span>
            {/if}
          </div>
        {/if}
        <MessageActionButtons
          {message}
          isBanned={message.banned}
          isMuted={message.muted}
          {overlaySocket}
          onModerationActive={(active) => {
            if (active) moderatingIds.add(message.id);
            else moderatingIds.delete(message.id);
          }}
        />
      </li>
    {/each}
  </ul>
  {#if scrolling && !moderationActive}
    <button
      in:fly={{ y: 100, duration: 100, delay: 100 }}
      class="btn scroll-to-bottom-btn"
      onclick={scrollToBottom}
    >
      <Pause /> Chat på pause
    </button>
  {/if}
  <WriteMessage {chatMessages} bind:selectedUser />
</div>

<style>
  .container {
    display: flex;
    position: relative;
    flex-direction: column;
    height: 100%;
    gap: 0.5rem;
  }
  .header {
    font-size: 1.25rem;
    font-weight: 600;
    padding-left: 0.5rem;
    margin-bottom: 1rem;
  }

  .user-counts {
    margin: 0;
    padding: 0;
    text-align: center;
    user-select: none;
  }

  .colored-user {
    background-color: var(--color-red-800);
  }
  .messages {
    flex: 1;
    list-style: none;
    padding: 0 1rem 0 0;
    overflow: hidden auto;
    margin: 0.5rem 0;
  }

  .message {
    display: flex;
    flex-direction: column;
    position: relative;
    word-break: break-word;
    border-bottom: 1px solid #333;
    padding-left: 0.1rem;
    background-color: var(--color-zinc-800);
    transition: background-color 250ms ease;
  }

  .message.highlighted {
    background-color: rgba(3, 105, 161, 0.2);
  }

  .message.unmuted {
    border-left: 0.25rem solid var(--color-sky-600);
  }

  .message.unbanned {
    border-left: 0.25rem solid var(--color-green-800);
  }

  .message.muted {
    border-left: 0.25rem solid var(--color-amber-400);
  }

  .message.banned {
    border-left: 0.25rem solid var(--color-red-800);
  }

  .message.faded {
    opacity: 0.6;
  }

  .message.severe {
    border-left: 0.25rem solid var(--color-red-500);
    background-color: rgba(239, 68, 68, 0.15);
  }

  .message-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }

  .message-content {
    flex: 1;
    min-width: 0;
  }

  .message-info {
    font-size: 0.8rem;
    color: var(--color-zinc-400);
    margin-top: 0.25rem;
    display: flex;
    gap: 0.5rem;
  }

  .message-info.red {
    color: var(--color-red-500);
  }

  .message-info.faded {
    color: var(--color-zinc-500);
  }

  .btn {
    display: flex;
    gap: 0.25rem;
    padding: 0.5rem 1rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    cursor: pointer;
    width: max-content;
  }
  .btn:hover {
    background-color: var(--color-zinc-600);
  }
  .btn:focus {
    outline: none;
    border-color: var(--color-zinc-400);
    border-width: 1px;
    border-style: solid;
    border-radius: 0.5rem;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .load-more-btn {
    align-self: center;
    font-size: 1rem;
    transition: background-color 0.2s ease;
  }

  .scroll-to-bottom-btn {
    position: absolute;
    bottom: 3.5rem;
    left: 50%;
    transform: translateX(-50%);
    align-items: center;
    justify-content: center;
    z-index: 2;
  }

  .name-btn {
    background: none;
    border: none;
    color: #64b5f6;
    cursor: pointer;
    font-weight: bold;
    font-size: 0.95rem;
    padding: 0;
  }
  .name-btn:hover {
    text-decoration: underline;
  }

  .name-btn.admin-user {
    font-weight: bold;
    font-size: 0.9rem;
    padding: 0.2rem 0.3rem;
    color: black !important;
    background: #a3e635;
    border-radius: 5px;
    margin: 0.25rem 0;
  }

  .sent-time {
    font-size: 0.7rem;
    color: var(--color-zinc-500);
    user-select: none;
  }

  .automoderated {
    display: inline-flex;
    gap: 0.25rem;
    font-size: 0.6rem;
    padding: 0.15rem 0.5rem;
    user-select: none;
    flex-shrink: 0;
  }
</style>
